<template>
    <div :class="['connection-status-card', `view-${viewMode}`]">
        <!-- Card View (Vertical Layout) -->
        <template v-if="viewMode === 'cards'">
            <div class="card-header">
                <div class="branch-info">
                    <i class="fas fa-building primary-text me-2"></i>
                    <h5 class="branch-name">{{ branch.name }}</h5>
                </div>
            </div>

            <div class="card-subheader">
                <span class="branch-code">
                    <i class="fas fa-tag primary-text me-1"></i>
                    {{ branch.code }}
                </span>
                <span :class="['status-badge', statusClass]">
                    <i :class="statusIcon"></i>
                    {{ statusText }}
                </span>
            </div>

            <div class="card-body">
                <div class="info-row">
                    <span class="label">
                        <i class="fas fa-store me-2"></i>
                        Merchant
                    </span>
                    <span class="value">{{ merchantName }}</span>
                </div>
                <div v-if="connection && connection.property_id" class="info-row">
                    <span class="label">
                        <i class="fas fa-key me-2"></i>
                        Property ID
                    </span>
                    <span class="value">{{ connection.property_id }}</span>
                </div>
                <div class="info-row">
                    <span class="label">
                        <i class="fas fa-link me-2"></i>
                        Connection Type
                    </span>
                    <span :class="['badge-type', connectionTypeClass]">
                        {{ connectionType }}
                    </span>
                </div>
                <div class="info-row">
                    <span class="label">
                        <i class="fas fa-clock me-2"></i>
                        Last Updated
                    </span>
                    <span class="value">{{ formattedTimestamp }}</span>
                </div>
            </div>

            <div class="card-footer">
                <button v-if="!connection" @click="$emit('setup', branch)" class="btn-action">
                    <i class="fas fa-plug me-1"></i>
                    Setup Connection
                </button>
                <button v-else-if="connectionType === 'Legacy'" @click="$emit('setup', branch)" class="btn-action">
                    <i class="fas fa-plus-circle me-1"></i>
                    Set New Connection
                </button>
                <button v-else @click="$emit('view', connection)" class="btn-action-secondary">
                    <i class="fas fa-eye me-1"></i>
                    View Details
                </button>
            </div>
        </template>

        <!-- List View (Horizontal Layout) -->
        <template v-else>
            <div class="list-left">
                <div class="list-name">
                    <i class="fas fa-building primary-text me-2"></i>
                    {{ branch.name }}
                </div>
                <div class="list-code">
                    <i class="fas fa-tag primary-text me-1"></i>
                    {{ branch.code }}
                </div>
            </div>

            <div class="list-status">
                <span :class="['status-badge', statusClass]">
                    <i :class="statusIcon"></i>
                    {{ statusText }}
                </span>
            </div>

            <div class="list-details">
                <div class="detail-item">
                    <i class="fas fa-store me-1"></i>
                    <span class="label">Merchant:</span>
                    <span class="value">{{ merchantName }}</span>
                </div>
                <div v-if="connection && connection.property_id" class="detail-item">
                    <i class="fas fa-key me-1"></i>
                    <span class="label">Property ID:</span>
                    <span class="value">{{ connection.property_id }}</span>
                </div>
                <div class="detail-item">
                    <i class="fas fa-link me-1"></i>
                    <span class="label">Type:</span>
                    <span :class="['badge-type', connectionTypeClass]">{{ connectionType }}</span>
                </div>
                <div class="detail-item">
                    <i class="fas fa-clock me-1"></i>
                    <span class="label">Updated:</span>
                    <span class="value">{{ formattedTimestamp }}</span>
                </div>
            </div>

            <div class="list-right">
                <button v-if="!connection" @click="$emit('setup', branch)" class="btn-action">
                    <i class="fas fa-plug me-1"></i>
                    Setup Connection
                </button>
                <button v-else-if="connectionType === 'Legacy'" @click="$emit('setup', branch)" class="btn-action">
                    <i class="fas fa-plus-circle me-1"></i>
                    Set New Connection
                </button>
                <button v-else @click="$emit('view', connection)" class="btn-action-secondary">
                    <i class="fas fa-eye me-1"></i>
                    View Details
                </button>
            </div>
        </template>
    </div>
</template>

<script>
import { computed } from 'vue'

export default {
    name: 'ConnectionStatusCard',
    props: {
        viewMode: {
            type: String,
            default: 'cards',
            validator: (value) => ['cards', 'list'].includes(value)
        },
        branch: {
            type: Object,
            required: true
        },
        connection: {
            type: Object,
            default: null
        },
        connectionType: {
            type: String,
            default: 'New'
        }
    },
    emits: ['setup', 'view'],
    setup(props) {
        const merchantName = computed(() => {
            if (props.connection && props.connection.merchant_name) {
                return props.connection.merchant_name
            }
            return '-'
        })

        const statusClass = computed(() => {
            return props.connection ? 'status-connected' : 'status-disconnected'
        })

        const statusText = computed(() => {
            return props.connection ? 'Connected' : 'Not Connected'
        })

        const statusIcon = computed(() => {
            return props.connection ? 'fas fa-check-circle' : 'fas fa-times-circle'
        })

        const connectionTypeClass = computed(() => {
            return props.connectionType === 'Legacy' ? 'badge-legacy' : 'badge-new'
        })

        const formattedTimestamp = computed(() => {
            if (!props.connection || !props.connection.updated_at) {
                return 'Never'
            }

            const date = new Date(props.connection.updated_at)
            const now = new Date()
            const diffMs = now - date
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

            if (diffDays === 0) {
                return 'Today'
            } else if (diffDays === 1) {
                return 'Yesterday'
            } else if (diffDays < 7) {
                return `${diffDays} days ago`
            } else {
                return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                })
            }
        })

        return {
            merchantName,
            statusClass,
            statusText,
            statusIcon,
            connectionTypeClass,
            formattedTimestamp
        }
    }
}
</script>

<style scoped>
/* Base Card Styles */
.primary-text {
    color: var(--primary-color, #F93E3E);
}

.connection-status-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.2s ease;
}

.connection-status-card:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

/* ========== CARD VIEW (Vertical) ========== */
.connection-status-card.view-cards {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.connection-status-card.view-cards:hover {
    transform: translateY(-2px);
}

.card-header {
    padding: 1.25rem;
    border-bottom: 1px solid #f3f4f6;
}

.branch-info {
    display: flex;
    align-items: center;
}

.branch-name {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
}

.card-subheader {
    padding: 0.875rem 1.25rem;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.branch-code {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
    display: flex;
    align-items: center;
}

.card-body {
    padding: 1.25rem;
    flex: 1;
}

.info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f9fafb;
}

.info-row:last-child {
    border-bottom: none;
}

.info-row .label {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
    display: flex;
    align-items: center;
}

.info-row .label i {
    color: #9ca3af;
}

.info-row .value {
    font-size: 0.875rem;
    color: #111827;
    font-weight: 600;
    text-align: right;
}

.card-footer {
    padding: 1rem 1.25rem;
    background: #f9fafb;
    border-top: 1px solid #e5e7eb;
    margin-top: auto;
}

.card-footer .btn-action,
.card-footer .btn-action-secondary {
    width: 100%;
}

/* ========== LIST VIEW (Horizontal) ========== */
.connection-status-card.view-list {
    display: grid;
    grid-template-columns: 2fr auto 3fr auto;
    gap: 1.5rem;
    align-items: center;
    padding: 1.25rem;
}

.connection-status-card.view-list:hover {
    background: #f9fafb;
}

.list-left {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
}

.list-name {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    display: flex;
    align-items: center;
}

.list-code {
    font-size: 0.8125rem;
    color: #6b7280;
    font-weight: 500;
    display: flex;
    align-items: center;
}

.list-status {
    display: flex;
    align-items: center;
}

.list-details {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
}

.detail-item {
    font-size: 0.8125rem;
    display: flex;
    align-items: center;
    gap: 0.375rem;
}

.detail-item i {
    color: #9ca3af;
    font-size: 0.75rem;
}

.detail-item .label {
    color: #6b7280;
    font-weight: 500;
}

.detail-item .value {
    color: #111827;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
}

.list-right {
    display: flex;
    align-items: center;
    justify-content: center;
}

.list-right .btn-action,
.list-right .btn-action-secondary {
    min-width: 180px;
}


/* Status Badges */
.status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.75rem;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    white-space: nowrap;
}

.status-connected {
    background: #d1fae5;
    color: #065f46;
}

.status-disconnected {
    background: #fee2e2;
    color: #991b1b;
}

/* Connection Type Badges */
.badge-type {
    padding: 0.25rem 0.625rem;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
    display: inline-block;
}

.badge-new {
    background: color-mix(in srgb, var(--primary-color), transparent 90%);
    color: var(--primary-color, #F93E3E);
}

.badge-legacy {
    background: #fef3c7;
    color: #92400e;
}

/* Buttons */
.btn-action {
    background: var(--primary-color, #F93E3E);
    color: white;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
}

.btn-action:hover {
    filter: brightness(0.9);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-action-secondary {
    background: #6b7280;
    color: white;
    border: none;
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
}

.btn-action-secondary:hover {
    background: #38414e;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}


/* Responsive */
@media (max-width: 1024px) {
    .connection-status-card.view-list {
        grid-template-columns: 1fr;
        gap: 1rem;
    }

    .list-details {
        grid-template-columns: 1fr;
    }

    .list-right {
        justify-content: flex-start;
    }

    .btn-action {
        width: 100%;
    }
}
</style>
